/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation, useQuery, useQueryClient } from 'react-query'

import type { ErrorResponse } from '@modules/project-user-api/v1/types'

export const DELETE_PROJECT_ERROR_RESPONSE_KEY = 'DELETE_PROJECT_ERROR_RESPONSE'

export const useDeleteProjectErrorQuery = () =>
  useQuery<ErrorResponse | null>({
    queryKey: [DELETE_PROJECT_ERROR_RESPONSE_KEY],
    queryFn: () =>
      new Promise((resolve, reject) => {
        try {
          const errorResponse = sessionStorage.getItem(DELETE_PROJECT_ERROR_RESPONSE_KEY)

          if (errorResponse === null) {
            resolve(null)

            return
          }

          resolve(JSON.parse(errorResponse))
        } catch (error) {
          reject(new Error('Error retrieving delete project error: ' + error))
        }
      }),
  })

export const useDeleteProjectErrorMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (errorResponse: ErrorResponse | null) =>
      new Promise((resolve, reject) => {
        try {
          if (errorResponse === null) {
            sessionStorage.removeItem(DELETE_PROJECT_ERROR_RESPONSE_KEY)
          } else {
            sessionStorage.setItem(DELETE_PROJECT_ERROR_RESPONSE_KEY, JSON.stringify(errorResponse))
          }

          resolve(true)
        } catch (error) {
          reject(new Error('Error saving delete project error: ' + error))
        }
      }),
    mutationKey: [DELETE_PROJECT_ERROR_RESPONSE_KEY],
    onSuccess: () => {
      queryClient.invalidateQueries([DELETE_PROJECT_ERROR_RESPONSE_KEY])
    },
  })
}
